import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  CdkDragDrop,
  CdkDropList,
  CdkDrag,
  CdkDragPlaceholder,
  CdkDragPreview,
} from '@angular/cdk/drag-drop';

import { ChartRendererService } from '../../shared-services/chart-renderer.service';
import { DockStateService } from '../../shared-services/dock-state.service';
import { PipelineWorkflowService } from '../../shared-services/pipeline-workflow.service';
import {
  Approver,
  DelegationUser,
  EscalationStep,
  WorkflowConfig,
  WorkflowNode,
  NodeType,
} from '../../shared-services/pipeline-workflow.model';

type PipelineWidgetTarget = {
  type: string;
  data: any;
};

@Component({
  selector: 'app-pipeline-widget-host',
  standalone: true,
  template: '',
})
export class PipelineWidgetHostComponent implements OnChanges, OnDestroy {
  @Input() target: PipelineWidgetTarget | null = null;

  private renderedSignature = '';

  constructor(
    private readonly viewContainerRef: ViewContainerRef,
    private readonly chartRendererService: ChartRendererService,
  ) {}

  ngOnChanges(_: SimpleChanges): void {
    const target = this.target;
    if (!target?.type) {
      this.clearRenderedComponent();
      return;
    }

    const signature = this.getTargetSignature(target);
    if (signature === this.renderedSignature) return;

    this.renderedSignature = signature;
    this.chartRendererService.renderChart(target.type, this.viewContainerRef, this.cloneObject(target.data));
  }

  ngOnDestroy(): void {
    this.clearRenderedComponent();
  }

  private clearRenderedComponent(): void {
    this.viewContainerRef.clear();
    this.renderedSignature = '';
  }

  private getTargetSignature(target: PipelineWidgetTarget): string {
    try {
      return JSON.stringify({ type: target.type, data: target.data }) || target.type;
    } catch {
      return target.type;
    }
  }

  private cloneObject<T>(value: T): T {
    if (Array.isArray(value)) return value.map((item) => this.cloneObject(item)) as T;
    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }
    return value;
  }
}

@Component({
  selector: 'app-pipeline-workflow-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CdkDropList, CdkDrag, CdkDragPlaceholder, CdkDragPreview,
    PipelineWidgetHostComponent,
  ],
  templateUrl: './pipeline-workflow-builder.component.html',
  styleUrl: './pipeline-workflow-builder.component.scss',
})
export class PipelineWorkflowBuilderComponent implements OnInit, OnDestroy {
  @Input() data: any;

  config: WorkflowConfig | null = null;
  activeNodeId: string | null = null;
  showAddModal = false;
  toastVisible = false;
  toastMessage = '';
  saveStatus: 'idle' | 'saving' | 'saved' = 'idle';
  dockSide: 'left' | 'right' = 'left';
  searchTerm = '';

  private _sub = new Subscription();
  private _toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly workflowService: PipelineWorkflowService,
    private readonly dockState: DockStateService,
    private readonly http: HttpClient,
  ) {}

  ngOnInit(): void {
    if (this.data) this.workflowService.loadConfig(this.data);

    this._sub.add(
      this.workflowService.config$.subscribe((cfg) => {
        this.config = cfg;
        if (this.activeNodeId && !cfg?.nodes?.some((n) => n.id === this.activeNodeId)) {
          this.activeNodeId = null;
        }
        if (!this.activeNodeId && cfg?.nodes?.length) {
          this.activeNodeId = cfg.nodes[0].id;
        }
      }),
    );

    this._sub.add(
      this.dockState.side$.subscribe((side) => {
        this.dockSide = side;
      }),
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    if (this._toastTimer !== null) clearTimeout(this._toastTimer);
  }

  get activeNode(): WorkflowNode | undefined {
    if (!this.activeNodeId || !this.config?.nodes) return undefined;
    return this.config.nodes.find((n) => n.id === this.activeNodeId);
  }

  get filteredNodes(): WorkflowNode[] {
    const nodes = this.config?.nodes || [];
    const query = this.searchTerm.trim().toLowerCase();
    if (!query) return nodes;

    return nodes.filter((node) => {
      const searchable = [
        node.id,
        node.type,
        node.label,
        node.decisionRule?.description,
        ...(node.triggerConditions || []).flatMap((condition) => [
          condition.field,
          condition.operator,
          String(condition.value ?? ''),
        ]),
        ...(node.approvers || []).map((approver) => approver.name),
        node.metadata?.usedBy,
      ];
      return searchable.some((value) => String(value || '').toLowerCase().includes(query));
    });
  }

  get activeNodeIndex(): number {
    if (!this.activeNodeId || !this.config?.nodes) return 0;
    return this.config.nodes.findIndex((n) => n.id === this.activeNodeId);
  }

  onHoverNode(nodeId: string): void {
    this.activeNodeId = nodeId;
  }

  dropNode(event: CdkDragDrop<WorkflowNode[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    this.workflowService.reorderNodes(event.previousIndex, event.currentIndex);
  }

  addNewNode(type: NodeType): void {
    this.workflowService.addNode(type);
    this.showAddModal = false;
    const labels: Record<NodeType, string> = {
      AUTO_APPROVE: 'Auto-approve', PARALLEL: 'Parallel gate', HIERARCHY: 'Hierarchy chain',
    };
    this.showToast(`${labels[type]} node added`);
  }

  onDeleteNode(id: string): void {
    this.workflowService.deleteNode(id);
    if (this.activeNodeId === id) this.activeNodeId = null;
    this.showToast('Node deleted');
  }

  onDuplicateNode(id: string): void {
    this.workflowService.duplicateNode(id);
    this.showToast('Node duplicated');
  }

  onTestNode(_id: string): void {
    this.showToast('Test run initiated');
  }

  onUpdateNode(node: WorkflowNode): void {
    this.workflowService.updateNode(node);
  }

  publish(): void {
    this.saveStatus = 'saving';
    this.workflowService.publish().subscribe({
      next: () => { this.saveStatus = 'saved'; this.showToast('Workflow published successfully'); },
      error: () => { this.saveStatus = 'idle'; this.showToast('Publish failed — please try again'); },
    });
  }

  validate(): void {
    const url = this.validateApiUrl;
    if (url && this.config) {
      this.http.post(url, this.buildSavePayload(), { headers: { 'Content-Type': 'application/json' } }).subscribe({
        next: () => this.showToast('Validation passed'),
        error: () => this.showToast('Validation failed - please review workflow'),
      });
      return;
    }

    const result = this.workflowService.validate();
    this.showToast(result.valid ? 'Validation passed' : `${result.warnings.length} warning(s) found`);
  }

  save(): void {
    const url = this.saveApiUrl;
    if (!url || !this.config) {
      this.saveStatus = 'saving';
      window.setTimeout(() => {
        this.saveStatus = 'saved';
        this.showToast('Workflow saved');
      }, 350);
      return;
    }

    this.saveStatus = 'saving';
    this.http.post(url, this.buildSavePayload(), { headers: { 'Content-Type': 'application/json' } }).subscribe({
      next: () => {
        this.saveStatus = 'saved';
        this.showToast('Workflow saved');
      },
      error: () => {
        this.saveStatus = 'idle';
        this.showToast('Save failed - please try again');
      },
    });
  }

  navigateBack(): void {
    if (typeof history !== 'undefined') history.back();
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.showAddModal = false;
    }
  }

  showToast(msg: string): void {
    if (this._toastTimer !== null) { clearTimeout(this._toastTimer); this._toastTimer = null; }
    this.toastMessage = msg;
    this.toastVisible = true;
    this._toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this._toastTimer = null;
    }, 3000);
  }

  getFlowNodeTarget(node: WorkflowNode, index: number): PipelineWidgetTarget | null {
    const widget = this.resolveChildWidgetConfig('flowNode');
    if (!widget) return null;
    return {
      type: widget.type,
      data: {
        ...widget.data,
        node,
        index,
        config: widget.data?.config ?? widget.data,
        isActive: node.id === this.activeNodeId,
        onNodeClick: (nodeId: string) => this.onHoverNode(nodeId),
        onInsertBefore: () => { this.showAddModal = true; },
      },
    };
  }

  getNodeCardTarget(node: WorkflowNode): PipelineWidgetTarget | null {
    const widget = this.resolveChildWidgetConfig('nodeCard');
    if (!widget) return null;
    return {
      type: widget.type,
      data: {
        ...widget.data,
        node,
        index: this.activeNodeIndex,
        config: widget.data?.config ?? widget.data,
        onUpdateNode: (updatedNode: WorkflowNode) => this.onUpdateNode(updatedNode),
        onDeleteNode: (nodeId: string) => this.onDeleteNode(nodeId),
        onDuplicateNode: (nodeId: string) => this.onDuplicateNode(nodeId),
        onTestNode: (nodeId: string) => this.onTestNode(nodeId),
      },
    };
  }

  get builderUi(): any {
    return this.data?.ui && typeof this.data.ui === 'object' ? this.data.ui : {};
  }

  get showNodesHeader(): boolean {
    return this.readFlag(this.builderUi?.nodesHeader?.visible, true);
  }

  get nodesHeaderTitle(): string {
    return this.readText(this.builderUi?.nodesHeader?.title, 'Configured nodes');
  }

  get nodesHeaderMeta(): string {
    const configured = this.readText(this.builderUi?.nodesHeader?.meta, '');
    if (configured) {
      return configured
        .replace('{{nodeCount}}', String(this.config?.nodes?.length ?? 0))
        .replace('{{lastEditedAgo}}', String(this.config?.lastEditedAgo ?? ''));
    }

    const lastEdited = this.config?.lastEditedAgo ? ` - last edited ${this.config.lastEditedAgo}` : '';
    return `${this.config?.nodes?.length ?? 0} nodes${lastEdited} - hover any node to expand`;
  }

  get addNodeButtonLabel(): string {
    return this.readText(this.builderUi?.nodesHeader?.addButtonLabel, 'Add');
  }

  get showHeaderSearch(): boolean {
    return this.readFlag(this.builderUi?.nodesHeader?.search?.visible, true);
  }

  get searchPlaceholder(): string {
    return this.readText(this.builderUi?.nodesHeader?.search?.placeholder, 'Search nodes...');
  }

  get showValidateAction(): boolean {
    return this.readFlag(this.builderUi?.nodesHeader?.actions?.validate?.visible, true);
  }

  get validateActionLabel(): string {
    return this.readText(this.builderUi?.nodesHeader?.actions?.validate?.label, 'Validate');
  }

  get showSaveAction(): boolean {
    return this.readFlag(this.builderUi?.nodesHeader?.actions?.save?.visible, true);
  }

  get saveActionLabel(): string {
    return this.readText(this.builderUi?.nodesHeader?.actions?.save?.label, 'Save');
  }

  get saveApiUrl(): string {
    return this.readText(this.data?.saveApi?.url, '');
  }

  get validateApiUrl(): string {
    return this.readText(this.data?.validateApi?.url, '');
  }

  get startTerminal(): any {
    return this.builderUi?.startTerminal && typeof this.builderUi.startTerminal === 'object'
      ? this.builderUi.startTerminal
      : {};
  }

  get endTerminal(): any {
    return this.builderUi?.endTerminal && typeof this.builderUi.endTerminal === 'object'
      ? this.builderUi.endTerminal
      : {};
  }

  get emptyStateConfig(): any {
    return this.builderUi?.emptyState && typeof this.builderUi.emptyState === 'object'
      ? this.builderUi.emptyState
      : {};
  }

  get detailEmptyStateConfig(): any {
    return this.builderUi?.detailEmptyState && typeof this.builderUi.detailEmptyState === 'object'
      ? this.builderUi.detailEmptyState
      : {};
  }

  get addNodeModalConfig(): any {
    return this.builderUi?.addNodeModal && typeof this.builderUi.addNodeModal === 'object'
      ? this.builderUi.addNodeModal
      : {};
  }

  get showBottomInsert(): boolean {
    return this.readFlag(this.builderUi?.bottomInsert?.visible, true);
  }

  get showEmptyState(): boolean {
    return this.readFlag(this.emptyStateConfig?.visible, true);
  }

  get showDetailEmptyState(): boolean {
    return this.readFlag(this.detailEmptyStateConfig?.visible, true);
  }

  get nodeTypeOptions(): Array<{ type: NodeType; title: string; description: string }> {
    const configured = this.addNodeModalConfig?.nodeTypes;
    if (Array.isArray(configured) && configured.length) {
      return configured
        .map((entry: any) => ({
          type: this.normalizeNodeType(entry?.type),
          title: this.readText(entry?.title, this.defaultNodeTypeTitle(this.normalizeNodeType(entry?.type))),
          description: this.readText(entry?.description, this.defaultNodeTypeDescription(this.normalizeNodeType(entry?.type))),
        }))
        .filter((entry) => !!entry.type);
    }

    return [
      { type: 'AUTO_APPROVE', title: 'Auto-approve', description: 'Automatically route without human review' },
      { type: 'PARALLEL', title: 'Parallel gate', description: 'Multiple approvers review simultaneously' },
      { type: 'HIERARCHY', title: 'Hierarchy chain', description: 'Sequential approval up the management chain' },
    ];
  }

  private resolveChildWidgetConfig(
    key: 'flowNode' | 'nodeCard',
  ): PipelineWidgetTarget | null {
    const directKey = key === 'flowNode' ? 'flowNodeWidget' : 'nodeCardWidget';
    const rawConfig = this.data?.[directKey] ?? this.data?.components?.[key];
    const type = typeof rawConfig?.type === 'string' ? rawConfig.type.trim() : '';
    if (!type) return null;
    const data = rawConfig && typeof rawConfig === 'object' && !Array.isArray(rawConfig)
      ? this.cloneObject(rawConfig)
      : {};

    delete data.type;
    return { type, data };
  }

  readFlag(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  readText(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }

  private normalizeNodeType(value: unknown): NodeType {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'PARALLEL' || normalized === 'HIERARCHY') {
      return normalized;
    }
    return 'AUTO_APPROVE';
  }

  private defaultNodeTypeTitle(type: NodeType): string {
    return type === 'PARALLEL' ? 'Parallel gate' : type === 'HIERARCHY' ? 'Hierarchy chain' : 'Auto-approve';
  }

  private defaultNodeTypeDescription(type: NodeType): string {
    return type === 'PARALLEL'
      ? 'Multiple approvers review simultaneously'
      : type === 'HIERARCHY'
        ? 'Sequential approval up the management chain'
        : 'Automatically route without human review';
  }

  private buildSavePayload(): any {
    const config = this.config as WorkflowConfig;
    const source = config.apiWorkflow || {};
    return {
      id: source.id ?? config.id,
      name: source.name ?? config.name,
      description: source.description ?? '',
      version: this.versionNumber(source.version ?? config.version),
      status: source.status ?? config.status,
      targetEntity: source.targetEntity ?? '',
      previousWFId: source.previousWFId ?? '',
      nextWFId: source.nextWFId ?? '',
      behavior: {
        onReject: source.behavior?.onReject ?? 'STOP',
        onTimeoutDefault: source.behavior?.onTimeoutDefault ?? 'ESCALATE',
        commentRequiredOnReject: source.behavior?.commentRequiredOnReject ?? true,
        allowRequesterToCancel: source.behavior?.allowRequesterToCancel ?? true,
      },
      approvalFlow: (config.nodes || []).map((node) => this.serializeApprovalFlowNode(node)),
    };
  }

  private serializeApprovalFlowNode(node: WorkflowNode): any {
    return {
      id: node.apiApprovalFlow?.id ?? '',
      triggerConditions: this.serializeTriggerConditions(node),
      approvalType: [
        {
          type: node.type,
          spec: node.type === 'HIERARCHY'
            ? this.serializeHierarchySpecs(node)
            : this.serializeNodeSpec(node),
        },
      ],
    };
  }

  private serializeHierarchySpecs(node: WorkflowNode): any[] {
    const originalApprovalType = Array.isArray(node.apiApprovalFlow?.approvalType)
      ? node.apiApprovalFlow.approvalType[0]
      : node.apiApprovalFlow?.approvalType;
    const originalSpecs = Array.isArray(originalApprovalType?.spec) ? originalApprovalType.spec : [];
    const levels = originalSpecs.length ? originalSpecs : [{ level: '1' }];
    return levels.map((spec: any, index: number) => ({
      ...this.serializeNodeSpec(node),
      level: String(spec?.level ?? index + 1),
    }));
  }

  private serializeNodeSpec(node: WorkflowNode): any {
    return {
      approvers: this.serializePeople(node.approvers),
      initDelegation: Boolean(node.delegationEnabled || node.delegationUsers?.length),
      delegateApprover: this.serializePeople(node.delegationUsers || []),
      rule: node.decisionRule?.type || 'ANY_ONE',
      minApprovals: Number(node.decisionRule?.minApprovals ?? 1),
      sla: {
        expired: false,
        reminderHours: Number(node.sla?.reminderHours ?? 0),
        onTimeout: node.sla?.onTimeout || 'ESCALATE',
        maxEscalation: Number(node.sla?.maxEscalations ?? 0),
        escalationsTriggered: this.originalEscalationsTriggered(node),
        escalationManagers: this.serializeEscalationManagers(node.sla?.escalationChain || []),
      },
    };
  }

  private serializeTriggerConditions(node: WorkflowNode): any {
    const conditions = node.triggerConditions || [];
    if (!conditions.length) return {};

    return conditions.reduce((acc: Record<string, any>, condition) => {
      const key = String(condition.field || condition.id || '').trim();
      if (!key) return acc;
      acc[key] = {
        operator: condition.operator || '=',
        value: condition.value ?? '',
        ...(condition.currency ? { currency: condition.currency } : {}),
      };
      return acc;
    }, {});
  }

  private serializePeople(people: Array<Approver | DelegationUser>): any[] {
    return (people || []).map((person) => ({
      kind: person.type || 'role',
      id: this.normalizeApiPersonId(person.id),
      label: person.name,
    }));
  }

  private serializeEscalationManagers(steps: EscalationStep[]): any[] {
    return (steps || []).map((step) => ({
      kind: 'role',
      id: step.id || '',
      label: step.name,
    }));
  }

  private originalEscalationsTriggered(node: WorkflowNode): string[] {
    const approvalType = Array.isArray(node.apiApprovalFlow?.approvalType)
      ? node.apiApprovalFlow.approvalType[0]
      : node.apiApprovalFlow?.approvalType;
    const specs = Array.isArray(approvalType?.spec) ? approvalType.spec : [approvalType?.spec];
    const triggered = specs.find((spec: any) => Array.isArray(spec?.sla?.escalationsTriggered))?.sla?.escalationsTriggered;
    return Array.isArray(triggered) ? triggered : [];
  }

  private normalizeApiPersonId(value: unknown): string[] {
    if (Array.isArray(value)) return value.map((item) => String(item));
    const text = String(value || '').trim();
    return text ? text.split(',').map((item) => item.trim()).filter(Boolean) : [];
  }

  private versionNumber(value: unknown): number {
    const parsed = Number(String(value ?? '').replace(/^v/i, ''));
    return Number.isFinite(parsed) ? parsed : 1;
  }

  private cloneObject<T>(value: T): T {
    if (Array.isArray(value)) return value.map((item) => this.cloneObject(item)) as T;
    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }
    return value;
  }
}
